import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (userData: any) => Promise<void>;
  googleLogin: (idToken: string, userType?: string) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const decodeJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to decode JWT token', e);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = decodeJwt(token);
          if (payload) {
            const isExpired = payload.exp * 1000 < Date.now();
            if (isExpired) {
              localStorage.removeItem('token');
            } else {
              const role = payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || payload.role;
              const firstName = payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"] || payload.given_name || payload.unique_name;
              const lastName = payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"] || payload.family_name || '';
              const email = payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] || payload.email;
              const id = payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] || payload.sub || payload.uid;

              setUser({
                id,
                email,
                firstName,
                lastName,
                role
              });
            }
          } else {
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Authentication check failed:', error);
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Login attempt with:', { email, password: '***' });
      console.log('API Base URL:', process.env.REACT_APP_API_URL || 'https://localhost:7245/api');
      console.log('Full API URL will be:', (process.env.REACT_APP_API_URL || 'https://localhost:7245/api') + '/Auth/login');
      
      const response = await api.login({ email, password });
      console.log('Login response:', response.data);
      
      const data = response.data.data as any;
      const token = data.Token || data.token;
      const user = {
        id: data.Id || data.id,
        email: data.Email || data.email,
        firstName: data.FirstName || data.firstName,
        lastName: data.LastName || data.lastName,
        role: data.UserType || data.userType
      };
      
      localStorage.setItem('token', token);
      setUser(user);
      
      // Redirect based on user role
      if (user.role === 'Instructor') {
        navigate('/instructor/dashboard');
      } else if (user.role === 'Admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/student/dashboard');
      }
    } catch (error: any) {
      console.error('Login failed - Full error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      throw error;
    }
  };

  const register = async (userData: any): Promise<void> => {
    try {
      const response = await api.register(userData);
      console.log('Registration response:', response.data);
      // Registration successful, but user needs to confirm email
      // Don't auto-login, just return success
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const googleLogin = async (idToken: string, userType?: string) => {
    try {
      const response = await api.googleLogin(idToken, userType);
      const responseData = response.data as any;
      
      if (responseData && responseData.registrationRequired) {
        return { registrationRequired: true, email: responseData.email };
      }

      const data = response.data.data;
      const token = data.Token;
      const user = {
        id: data.Id,
        email: data.Email,
        firstName: data.FirstName,
        lastName: data.LastName,
        role: data.UserType
      };
      
      localStorage.setItem('token', token);
      setUser(user);
      
      // Redirect based on user role
      if (user.role === 'Instructor') {
        navigate('/instructor/dashboard');
      } else if (user.role === 'Admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/student/dashboard');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    googleLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
